import { createRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import dayjs from 'dayjs';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import FormInput from 'components/FormInput';
import { RequestInstance } from 'request';
import { setUserInfo } from 'stores/actions/user';
import styles from './EmailForm.module.scss';

const EmailForm = ({
  className,
  controlId,
  ctaVariant = 'primary',
  emailAddressLabel,
  emailFormPlaceholder,
  formClassName = 'pt-8 bg-transparent',
  formGroupClassName,
  klaviyoCustomProperty,
  labelClassName,
  setSuccessSignUp,
}) => {
  const dispatch = useDispatch();

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const userInfo = useSelector((state) => state.user.userInfo);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);

  useEffect(() => {
    if (userInfo?.email) {
      setEmail(userInfo.email);
    }
  }, [userInfo?.email]);

  const formRef = createRef();

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitError(null);
    try {
      const body = { email, customPropertyName: klaviyoCustomProperty };

      const { klaviyoId: userKlaviyoId } = userInfo;

      if (userKlaviyoId) {
        body.profileId = userKlaviyoId;
      }

      const { klaviyoId } = await RequestInstance.post('api/email-signup', {
        json: body,
      });

      if (isLoggedIn && klaviyoId) {
        const {
          dateOfBirth,
          firstName,
          lastName,
          phoneNumber,
          preferredStore = '',
          zipCode = '',
        } = userInfo;
        const formattedDob = dayjs(dateOfBirth).format('YYYY-MM-DD');
        const formattedPhone = phoneNumber.replace('+1', '');
        /* eslint-disable camelcase */
        const body = {
          birthdate: formattedDob,
          family_name: lastName,
          given_name: firstName,
          is_enrolled_marketing: true,
          phone_number: formattedPhone,
          klaviyo_id: klaviyoId,
        };

        if (zipCode) {
          body.zip_code = zipCode;
        }

        if (preferredStore) {
          body.preferred_store = preferredStore;
        }
        if (userKlaviyoId) {
          const { userInfo: updatedUserInfo } = await RequestInstance.post(
            'api/account/user',
            {
              json: body,
            }
          );

          const { email, isEnrolledMarketing, klaviyoId } = updatedUserInfo;

          if (klaviyoId && isEnrolledMarketing) {
            await RequestInstance.post('api/marketing/unsuppress-profile', {
              json: { email },
            });
          }
        } else {
          await RequestInstance.post('api/account/user', {
            json: body,
          });
        }
      }

      setIsLoading(false);
      setSuccessSignUp(true);

      if (isLoggedIn) {
        const { userInfo: updatedUserInfo } = await RequestInstance.get(
          'api/account/user'
        );

        if (updatedUserInfo) {
          dispatch(setUserInfo(updatedUserInfo));
        }
      }
    } catch (err) {
      const responseData = await err?.response?.json;
      setIsLoading(false);
      setSuccessSignUp(false);
      const errorMessage =
        typeof responseData?.message === 'string'
          ? responseData.message.replace(
              'Use PUT to insert or update list members.',
              ''
            )
          : 'We are sorry, an error occured';
      setSubmitError(errorMessage);
    }
  };

  const handleOnChange = (e) => {
    setSubmitError(null);
    setEmail(e?.currentTarget?.value);
  };

  return (
    <>
      <Form
        ref={formRef}
        className={classNames(
          'd-flex flex-row',
          formClassName,
          styles.emailForm
        )}
        onSubmit={onSubmit}
      >
        <FormInput
          required
          className={classNames('bg-transparent', styles.emailInput, className)}
          controlId={controlId}
          defaultValue={userInfo?.email}
          feedbackStyles={`bg-white fs-5 fw-600 p-2 ${styles.feedback}`}
          formGroupClassName={formGroupClassName}
          label={emailAddressLabel}
          placeholder={emailFormPlaceholder}
          labelClassName={labelClassName}
          pattern="^\S+@\S+\.\S\S+$"
          type="email"
          onChange={handleOnChange}
        />
        <Button
          className={classNames(
            'fw-400 rounded mb-10 ms-10 w-100 fs-7 fs-sm-8 font-sofia text-uppercase',
            styles.submitButton
          )}
          id="email_capture_submit_button"
          type="submit"
          variant={ctaVariant}
          disabled={isLoading}
        >
          Sign Up
        </Button>
      </Form>
      {submitError && (
        <div
          className={`bg-white text-danger fs-5 fw-600 p-2 ${styles.feedback}`}
          role="alert"
          type="invalid"
        >
          {submitError}
        </div>
      )}
    </>
  );
};

export default EmailForm;
